import type { ScoringProfile } from '../../../lib/profiles';

/**
 * Cost profile — answers "where does the money go?"
 *
 * Buckets resources by monthly spend and highlights the top spenders.
 * Severity-as-cost mapping:
 *   - critical: > $1000/mo (top spender worth examining)
 *   - high:     > $500/mo
 *   - medium:   > $100/mo
 *   - low:      > $10/mo
 *
 * Thresholds are intentionally round; tune per customer later via a
 * config layer if needed. The idea isn't precision — it's surfacing
 * where a 30-second audit would start.
 */
export const CostProfile: ScoringProfile = {
  id: 'network-cost',
  name: 'Cost',
  description: 'Highlights top cost drivers and untagged spend. Requires network-cost field pack with monthlySpend populated (from AWS Cost Explorer).',
  domain: 'network',
  visibility: {
    fieldPacks: ['network-base', 'network-cost', 'network-tags'],
  },
  scoring: [
    {
      id: 'cost.very-high',
      category: 'Cost drivers',
      label: '> $1000 / month',
      severity: 'critical',
      target: 'node',
      whenNode: (_n, p) => typeof p.monthlySpend === 'number' && (p.monthlySpend as number) > 1000,
    },
    {
      id: 'cost.high',
      category: 'Cost drivers',
      label: '$500 – $1000 / month',
      severity: 'high',
      target: 'node',
      whenNode: (_n, p) => {
        const spend = p.monthlySpend as number | undefined;
        return typeof spend === 'number' && spend > 500 && spend <= 1000;
      },
    },
    {
      id: 'cost.medium',
      category: 'Cost drivers',
      label: '$100 – $500 / month',
      severity: 'medium',
      target: 'node',
      whenNode: (_n, p) => {
        const spend = p.monthlySpend as number | undefined;
        return typeof spend === 'number' && spend > 100 && spend <= 500;
      },
    },
    {
      // Override severity palette: low spend reads as "fine, not waste" — green
      // is more semantically useful here than the default grey for low severity.
      id: 'cost.low',
      category: 'Cost drivers',
      label: '$10 – $100 / month',
      severity: 'low',
      color: '#84cc16',
      target: 'node',
      whenNode: (_n, p) => {
        const spend = p.monthlySpend as number | undefined;
        return typeof spend === 'number' && spend > 10 && spend <= 100;
      },
    },
    {
      id: 'cost.no-center',
      category: 'Cost allocation',
      label: 'Missing cost center tag',
      description: 'Resource has monthly spend but no costCenter tag — unattributable cost.',
      severity: 'medium',
      target: 'node',
      whenNode: (_n, p) => {
        const spend = p.monthlySpend as number | undefined;
        const tags = (p.tags as Record<string, string>) ?? {};
        return typeof spend === 'number' && spend > 10 && !tags.costCenter && !tags.CostCenter;
      },
    },
    {
      id: 'cost.zombie-candidate',
      category: 'Waste',
      label: 'Likely zombie resource',
      description: 'Resource is billed but shows as stopped or has zero utilization. Candidate for deletion.',
      severity: 'high',
      target: 'node',
      whenNode: (_n, p) => {
        const spend = p.monthlySpend as number | undefined;
        return (
          typeof spend === 'number' &&
          spend > 0 &&
          (p.state === 'stopped' || p.utilizationPct === 0)
        );
      },
    },
  ],
};
